import { useMemo, useState } from "react";
import { useAccount, useConnect, useDisconnect, usePublicClient, useSignTypedData, useSwitchChain, useWalletClient } from "wagmi";
import { base } from "wagmi/chains";
import type { Address } from "viem";
import {
  MAX_X402_REVIEW_TEXT_BYTES,
  X402_RECEIPT_POLICY_VERSION,
  X402_SELF_REPORTED_POLICY_VERSION,
  parseX402ReceiptEvidenceBundle,
  parseX402Review,
  verifyX402ReceiptEvidence,
  x402ReceiptDigest,
  x402ReviewTypedData,
} from "@boon/x402-route";
import type { RoutedBoonRow } from "../../lib/api";
import { captureX402EndpointUrl, prepareX402ReviewPublication, publishX402Review, reviewableRecognitions, type X402PublicReview } from "../../lib/x402Discovery";
import { walletPaidFetch } from "../../lib/x402Pay";

function recognitionKey(row: { txHash: string; logIndex: string }): string {
  return row.txHash.toLowerCase() + ":" + row.logIndex;
}

export function X402ReviewComposer({
  contract,
  routeId,
  routeUrl,
  endpointMethod,
  receiptPayTo,
  receiptSigner,
  receiptStatus,
  recognitions,
  reviews,
  onPublished,
}: {
  contract: string;
  routeId: string;
  routeUrl: string;
  endpointMethod?: string;
  receiptPayTo: string;
  receiptSigner: string | null;
  receiptStatus: "available" | "not_observed";
  recognitions: RoutedBoonRow[] | null;
  reviews: X402PublicReview[];
  onPublished: () => void;
}) {
  const { address, isConnected, chainId, connector } = useAccount();
  const { switchChainAsync } = useSwitchChain();
  const { signTypedDataAsync } = useSignTypedData();
  const { connectAsync, connectors } = useConnect();
  const { disconnectAsync } = useDisconnect();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const [connecting, setConnecting] = useState<"coinbase" | "metamask" | "walletConnect" | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  // Mirror the send widget's connector resolution so the same three wallet
  // choices appear here as on the send page.
  const metamaskConnector = useMemo(
    () =>
      connectors.find((c) => /metamask/i.test(c.name) || c.id === "metaMask" || c.type === "metaMask") ??
      connectors.find((c) => c.type === "injected") ??
      connectors.find((c) => c.id === "injected") ??
      connectors.find((c) => /metamask|rabby|rainbow|brave|frame/i.test(c.name)),
    [connectors],
  );
  const coinbaseConnector = useMemo(
    () =>
      connectors.find((c) => c.id === "coinbaseWallet") ??
      connectors.find((c) => /coinbase/i.test(c.name)),
    [connectors],
  );
  const walletConnectConnector = useMemo(
    () =>
      connectors.find((c) => c.id === "walletConnect") ??
      connectors.find((c) => /walletconnect|reown/i.test(c.name)),
    [connectors],
  );
  const connectChoices = [
    { kind: "coinbase" as const, label: "Coinbase", connector: coinbaseConnector },
    { kind: "metamask" as const, label: "Metamask", connector: metamaskConnector },
    { kind: "walletConnect" as const, label: "WalletConnect", connector: walletConnectConnector },
  ].filter((choice) => choice.connector);
  async function connectWallet(choice: (typeof connectChoices)[number]) {
    if (!choice.connector || connecting) return;
    setConnecting(choice.kind);
    setConnectError(null);
    try {
      // Disconnect any stale session first so the chosen wallet actually becomes
      // the signer; otherwise wagmi keeps the previously-active connector (e.g.
      // MetaMask) and signs through it even when the user picked Coinbase.
      if (isConnected && connector?.id !== choice.connector.id) await disconnectAsync();
      await connectAsync({ connector: choice.connector, chainId: base.id });
    } catch (cause) {
      setConnectError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setConnecting(null);
    }
  }
  const [mode, setMode] = useState<"self" | "receipt" | "boon">("self");
  const [text, setText] = useState("");
  const [offerJson, setOfferJson] = useState("");
  const [receiptJson, setReceiptJson] = useState("");
  const [selected, setSelected] = useState("");
  const [confirmedUse, setConfirmedUse] = useState(false);
  const [status, setStatus] = useState<"idle" | "signing" | "publishing" | "prepared" | "published">("idle");
  const [error, setError] = useState<string | null>(null);
  const [prepared, setPrepared] = useState<ReturnType<typeof prepareX402ReviewPublication> | null>(null);
  const [confirmedText, setConfirmedText] = useState("");
  const byteCount = new TextEncoder().encode(text).byteLength;
  const eligible = useMemo(
    () => reviewableRecognitions(address, recognitions, reviews),
    [address, recognitions, reviews],
  );
  const eventKey = selected || (eligible[0] ? recognitionKey(eligible[0]) : "");
  const recognition = eligible.find((row) => recognitionKey(row) === eventKey) ?? null;

  // Lane availability, so the UI stops asking the user to know whether they
  // "have a receipt." Self-reported always works (wallet + fee). Boon-backed
  // unlocks once the connected wallet has an unreviewed routed Boon here.
  // Receipt-verified unlocks only when the provider exposes a signed offer.
  const boonAvailable = eligible.length > 0;
  const receiptAvailable = receiptStatus === "available";
  const lanes = [
    { id: "self" as const, label: "Self-reported · $0.05", available: true, locked: null },
    {
      id: "boon" as const,
      label: "Boon-backed",
      available: boonAvailable,
      locked: boonAvailable ? null : "Requires an unreviewed routed Boon from this wallet.",
    },
    {
      id: "receipt" as const,
      label: "Receipt-verified · $0.01",
      available: receiptAvailable,
      locked: receiptAvailable ? null : "Requires provider-signed offer and receipt JSON.",
    },
  ];

  async function publish() {
    if (!address || !confirmedUse) return;
    setError(null);
    setPrepared(null);
    try {
      if (chainId !== base.id) await switchChainAsync({ chainId: base.id });
      const createdAt = Math.floor(Date.now() / 1_000);
      let evidence;
      const review = mode === "self" ? parseX402Review({
        version: "3",
        policyVersion: X402_SELF_REPORTED_POLICY_VERSION,
        routeId,
        reviewer: address,
        reviewText: text,
        createdAt,
      }) : mode === "receipt" ? (() => {
        let offer: unknown;
        let receipt: unknown;
        try {
          offer = JSON.parse(offerJson);
          receipt = JSON.parse(receiptJson);
        } catch {
          throw new Error("Offer and receipt must each be valid JSON.");
        }
        evidence = parseX402ReceiptEvidenceBundle({ offer, receipt });
        if (evidence.receipt.payload.payer.toLowerCase() !== address.toLowerCase()) {
          throw new Error("Connected wallet must match receipt payer " + evidence.receipt.payload.payer + ".");
        }
        return parseX402Review({
          version: "2",
          policyVersion: X402_RECEIPT_POLICY_VERSION,
          routeId,
          receiptDigest: x402ReceiptDigest(evidence.receipt),
          reviewText: text,
          createdAt,
        });
      })() : (() => {
        if (!recognition) throw new Error("This wallet has no unreviewed routed Boon for the endpoint.");
        return parseX402Review({
          version: "1",
          routeId,
          recognitionTxHash: recognition.txHash,
          recognitionLogIndex: Number(recognition.logIndex),
          reviewText: text,
          createdAt,
        });
      })();

      if (mode === "receipt" && evidence && review.version === "2") {
        if (receiptStatus !== "available" || !receiptSigner) {
          throw new Error("This provider has not exposed an official route-authorized offer-receipt signer.");
        }
        await verifyX402ReceiptEvidence(evidence, {
          expectedPayer: address,
          expectedRouteUrl: routeUrl,
          expectedServiceSigner: receiptSigner as Address,
          expectedPayTo: receiptPayTo as Address,
          expectedReceiptDigest: review.receiptDigest,
          reviewCreatedAt: review.createdAt,
        });
      }

      setStatus("signing");
      const signature = await Promise.race([
        signTypedDataAsync({
          account: address,
          ...x402ReviewTypedData(review, {
            chainId: base.id,
            verifyingContract: contract as Address,
          }),
        }),
        new Promise<never>((_, reject) =>
          window.setTimeout(
            () => reject(new Error("The wallet did not return a signature. If a Coinbase Smart Wallet popup is spinning, reconnect with the Coinbase extension (an EOA account) and try again.")),
            90_000,
          ),
        ),
      ]);
      if (mode !== "boon") {
        const request = prepareX402ReviewPublication(review, signature, evidence);
        // Pay the $0.05/$0.01 fee inline with the connected wallet instead of
        // handing back a request to run elsewhere. Falls back to the copyable
        // request only when in-browser payment isn't possible or fails.
        if (walletClient && publicClient) {
          try {
            setStatus("publishing");
            const paidFetch = walletPaidFetch(walletClient, publicClient);
            const response = await paidFetch(request.endpoint, {
              method: "POST",
              headers: { "content-type": "application/json" },
              body: JSON.stringify({ review, signature, ...(evidence ? { evidence } : {}) }),
            });
            if (!response.ok) {
              const detail = await response.text().catch(() => "");
              throw new Error(`Publish failed (${response.status}). ${detail.slice(0, 200)}`);
            }
            setConfirmedText(text);
            setText("");
            setOfferJson("");
            setReceiptJson("");
            setSelected("");
            setConfirmedUse(false);
            setStatus("published");
            if (endpointMethod) void captureX402EndpointUrl(routeUrl, endpointMethod).catch(() => {});
            onPublished();
            return;
          } catch (payCause) {
            // Surface the reason, then fall through to the copyable request so
            // the user still has a working path (e.g. insufficient USDC).
            setError(`In-browser payment did not complete: ${payCause instanceof Error ? payCause.message : String(payCause)}. Use the ready-to-run request below instead.`);
          }
        }
        setPrepared(request);
        setStatus("prepared");
        return;
      }
      setStatus("publishing");
      await publishX402Review(review, signature);
      setConfirmedText(text);
      setText("");
      setOfferJson("");
      setReceiptJson("");
      setSelected("");
      setConfirmedUse(false);
      setStatus("published");
      if (endpointMethod) void captureX402EndpointUrl(routeUrl, endpointMethod).catch(() => {});
      onPublished();
    } catch (cause) {
      setStatus("idle");
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <section className="border border-faint bg-paper-deep/55 rounded-md p-5 md:p-6">
      <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-muted">Your experience</p>
      <h2 className="mt-2 text-2xl font-display tracking-tight">Share your experience</h2>
      <p className="mt-2 text-sm text-muted leading-relaxed max-w-3xl">
        Choose the strongest evidence available. Wallet reviews cost $0.05; Boon-backed reviews are free and ranked; provider-signed receipts cost $0.01 and remain unranked.
      </p>

      <div className="mt-5 flex flex-wrap gap-2" role="group" aria-label="Review evidence">
        {lanes.map((lane) => (
          <button
            key={lane.id}
            aria-pressed={mode === lane.id}
            aria-describedby={!lane.available ? `review-lane-${lane.id}-reason` : undefined}
            disabled={!lane.available}
            className={
              "btn-mono text-xs rounded-md border px-3 py-2 " +
              (mode === lane.id
                ? "border-olive bg-olive-soft text-olive-deep"
                : lane.available
                  ? "border-faint text-muted hover:text-ink hover:border-ink-soft"
                  : "border-faint/60 text-muted/50 cursor-not-allowed")
            }
            type="button"
            title={lane.locked ?? undefined}
            onClick={() => lane.available && setMode(lane.id)}
          >
            {lane.available ? lane.label : `${lane.label} · locked`}
          </button>
        ))}
      </div>
      {mode === "self" && (
        <p className="mt-2 text-xs text-muted">Recommended for a first review. No receipt or prior Boon required.</p>
      )}
      {lanes.map((lane) => !lane.available && lane.locked ? (
        <p key={lane.id} id={`review-lane-${lane.id}-reason`} className="mt-2 text-xs text-muted">
          {lane.label}: {lane.locked}
        </p>
      ) : null)}

      {!isConnected || !address ? (
        <div className="mt-5 space-y-3">
          <p className="text-sm text-muted">
            {mode === "self"
              ? "Connect any wallet to sign a self-reported public review."
              : mode === "receipt"
                ? "Connect the receipt-payer wallet to sign a receipt-verified public review."
                : "Connect the Boon-sender wallet to sign a Boon-backed public review."}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="btn-mono text-xs text-muted">Connect wallet</span>
            {connectChoices.map((choice) => (
              <button
                key={choice.kind}
                type="button"
                onClick={() => void connectWallet(choice)}
                disabled={connecting !== null}
                className="btn-mono text-xs px-2 py-1 rounded border border-faint text-muted hover:text-ink hover:border-ink-soft disabled:opacity-60"
              >
                {connecting === choice.kind ? "Connecting…" : choice.label}
              </button>
            ))}
          </div>
          {connectError ? <p className="text-xs text-red-700 break-words">{connectError}</p> : null}
        </div>
      ) : (
        <div className="mt-5 space-y-4 max-w-3xl">
          {mode === "self" ? (
            <p className="text-sm text-muted">
              This lane needs no provider receipt. Its five-cent publication fee is anti-spam, not endorsement. It proves neither purchase, use, nor quality and remains excluded from ranking.
            </p>
          ) : mode === "receipt" && receiptStatus !== "available" ? (
            <p className="text-sm text-muted">
              This provider has not exposed an official route-authorized x402 offer-receipt signer yet. You can still use the self-reported lane or leave a Boon-backed review after sending a Boon. A basic settlement header cannot support the receipt lane.
            </p>
          ) : mode === "receipt" ? (
            <div className="grid gap-4">
              <label className="block text-xs text-muted btn-mono">
                Signed x402 offer JSON
                <textarea
                  className="mt-2 w-full min-h-28 border border-faint rounded-md bg-paper px-4 py-3 text-xs text-ink font-mono resize-y"
                  value={offerJson}
                  onChange={(event) => setOfferJson(event.target.value)}
                  placeholder={'{"format":"eip712","payload":{...},"signature":"0x..."}'}
                />
              </label>
              <label className="block text-xs text-muted btn-mono">
                Signed x402 receipt JSON
                <textarea
                  className="mt-2 w-full min-h-28 border border-faint rounded-md bg-paper px-4 py-3 text-xs text-ink font-mono resize-y"
                  value={receiptJson}
                  onChange={(event) => setReceiptJson(event.target.value)}
                  placeholder={'{"format":"eip712","payload":{"payer":"0x..."},"signature":"0x..."}'}
                />
              </label>
              <p className="text-xs text-muted">The provider must return the official x402 EIP-712 offer-receipt extension. Boon checks both signatures against the pinned service signer and checks the signed recipient against the separately pinned Safe. A basic PAYMENT-RESPONSE header is not enough. Query strings and fragments are rejected so private request parameters are not published.</p>
            </div>
          ) : eligible.length > 0 ? (
            <label className="block text-xs text-muted btn-mono">
              Routed Boon receipt
              <select
                className="mt-2 w-full border border-faint rounded-md bg-paper px-3 py-3 text-ink"
                value={eventKey}
                onChange={(event) => setSelected(event.target.value)}
              >
                {eligible.map((row) => (
                  <option key={recognitionKey(row)} value={recognitionKey(row)}>
                    {row.txHash.slice(0, 10)}… · log {row.logIndex}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <p className="text-sm text-muted">This connected wallet has no unreviewed routed Boon for the endpoint. Use a visible unranked lane, or send a Boon first for ranked conviction.</p>
          )}
          <label className="block">
            <span className="sr-only">Short subjective review</span>
            <textarea
              className="w-full min-h-28 border border-faint rounded-md bg-paper px-4 py-3 text-ink placeholder:text-muted resize-y"
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="What was this endpoint useful for, and where did it fall short?"
              maxLength={MAX_X402_REVIEW_TEXT_BYTES}
            />
          </label>
          <label className="flex items-start gap-3 text-sm text-ink-soft leading-relaxed">
            <input className="mt-1" type="checkbox" checked={confirmedUse} onChange={(event) => setConfirmedUse(event.target.checked)} />
            <span>{mode === "self"
              ? "I understand this is a public wallet-signed opinion and does not prove I purchased or used the endpoint."
              : mode === "receipt"
                ? "I am publishing the signed receipt from the interaction described in this review."
                : "I used this endpoint through x402 before sending the cited Boon."}</span>
          </label>
          {(() => {
            const blockedReason = !text.trim()
              ? "Write a short review to continue."
              : !confirmedUse
                ? "Check the confirmation box to continue."
                : byteCount > MAX_X402_REVIEW_TEXT_BYTES
                  ? "The review is over the length limit."
                  : null;
            return blockedReason ? <p className="text-xs text-muted">{blockedReason}</p> : null;
          })()}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
            <span>Signing with <span className="text-ink">{connector?.name ?? "wallet"}</span> ({address.slice(0, 6)}…{address.slice(-4)}).</span>
            <span className="flex items-center gap-1">
              Switch:
              {connectChoices.map((choice) => (
                <button
                  key={choice.kind}
                  type="button"
                  onClick={() => void connectWallet(choice)}
                  disabled={connecting !== null}
                  className="btn-mono rounded border border-faint px-1.5 py-0.5 text-olive-deep hover:border-ink-soft disabled:opacity-60"
                >
                  {connecting === choice.kind ? "…" : choice.label}
                </button>
              ))}
            </span>
          </div>
          <div className="flex flex-wrap-reverse items-center justify-between gap-3">
            <button
              className="btn btn-primary"
              type="button"
              onClick={publish}
              disabled={!confirmedUse || !text.trim() || byteCount > MAX_X402_REVIEW_TEXT_BYTES ||
                (mode === "receipt" && (receiptStatus !== "available" || !receiptSigner)) ||
                (mode === "receipt" && (!offerJson.trim() || !receiptJson.trim())) ||
                (mode === "boon" && !recognition) || status === "signing" || status === "publishing"}
            >
              {status === "signing" ? "Signing…" : status === "publishing" ? "Paying and Publishing…" : mode === "self" ? (walletClient && publicClient ? "Sign and Pay $0.05 to Publish" : "Sign and Prepare $0.05 Request") : mode === "receipt" ? (walletClient && publicClient ? "Sign and Pay $0.01 to Publish" : "Sign and Prepare $0.01 Request") : "Sign and Publish Review"}
            </button>
            <span className={"btn-mono text-xs " + (byteCount > MAX_X402_REVIEW_TEXT_BYTES ? "text-danger" : "text-muted")}>
              {byteCount}/{MAX_X402_REVIEW_TEXT_BYTES} bytes
            </span>
          </div>
        </div>
      )}
      {status === "published" && (
        <div className="mt-5 animate-fade-up border border-olive/40 bg-olive-soft/30 rounded-md p-5">
          <div className="flex items-start gap-3">
            <span className="animate-check-pop mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-olive text-paper">
              <svg viewBox="0 0 20 20" fill="none" className="h-3.5 w-3.5" aria-hidden="true">
                <path d="M4 10.5l3.5 3.5L16 5.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <div className="min-w-0">
              <p className="text-sm font-display text-ink">Review published</p>
              <p className="mt-1 text-sm text-muted leading-relaxed">
                Your wallet-signed review of <span className="break-all text-ink">{routeUrl}</span> is live. It is attributed to your wallet and clearly labeled by evidence lane.
              </p>
              {confirmedText && (
                <blockquote className="mt-3 border-l-2 border-olive/40 pl-3 text-sm text-ink-soft italic break-words">"{confirmedText}"</blockquote>
              )}
              <button
                type="button"
                onClick={() => setStatus("idle")}
                className="mt-3 btn-mono text-xs rounded-md border border-faint px-3 py-1.5 text-olive-deep hover:border-ink-soft"
              >
                Write another review
              </button>
            </div>
          </div>
        </div>
      )}
      {status === "prepared" && prepared && (
        <div className="mt-5 border border-faint rounded-md bg-paper p-4">
          <p className="btn-mono text-xs uppercase tracking-[0.14em] text-olive-deep">Signed request ready</p>
          <p className="mt-2 text-sm text-muted">
            Submit this body to <code className="break-all">{prepared.endpoint}</code> with an x402 or MPP paying client. The publication price is {prepared.price}. AgentCash or the Boon CLI can complete the paid request.
          </p>
          <textarea aria-label="Signed review request body" className="mt-3 w-full min-h-40 border border-faint rounded-md bg-paper-deep px-3 py-3 text-xs font-mono" readOnly value={prepared.body} />
          <div className="mt-3 flex flex-wrap gap-2">
            <button className="btn btn-ghost" type="button" onClick={() => void navigator.clipboard.writeText(prepared.body)}>Copy signed request</button>
            <button className="btn btn-ghost" type="button" onClick={() => void navigator.clipboard.writeText(prepared.agentCashCommand)}>Copy AgentCash command</button>
          </div>
          <code className="mt-3 block break-all text-xs text-muted">{prepared.agentCashCommand}</code>
        </div>
      )}
      {error && <p className="mt-4 text-sm text-danger">{error}</p>}
    </section>
  );
}
